import { renderMarkdown } from '@/lib/markdown'
import type { TeamUpdate } from '@/lib/types'

/**
 * Turning a team update into an email (Towel #9).
 *
 * The "Email Update" button does not send anything. `mailto:` can only carry a
 * plain-text body, so the update is rendered to HTML and put on the clipboard,
 * and the mail client is opened with the subject filled in for the coach to
 * paste into. Nothing here talks to a server.
 *
 * ## Why the HTML carries its own styles
 *
 * On screen the update is styled by Tailwind classes on a wrapper element
 * (`Markdown` in components/page.tsx). None of that survives a paste: mail
 * clients drop `<style>` blocks and know nothing of our stylesheet, so a table
 * pasted as-is arrives with no borders — two columns of words that no longer
 * read as a comparison. Every element that needs a look gets it inline.
 *
 * The colours are DPL ink and neutrals from index.css, written out as literals
 * because an email is read in someone else's client, in someone else's theme:
 * it cannot follow ours, so it does not try.
 */

const INK = '#182239'
const RULE = '#d0d3d8'
const TINT = '#efefef'
const LINK = '#13612e'

const STYLES: Record<string, string> = {
  table: `border-collapse:collapse;width:100%;margin:8px 0`,
  th: `border:1px solid ${RULE};background:${TINT};padding:6px 10px;text-align:left;font-weight:600`,
  td: `border:1px solid ${RULE};padding:6px 10px;vertical-align:top`,
  h2: `font-size:16px;font-weight:600;margin:18px 0 6px`,
  h3: `font-size:14px;font-weight:600;margin:14px 0 4px`,
  h4: `font-size:14px;font-weight:600;margin:12px 0 4px`,
  p: `margin:0 0 10px`,
  ul: `margin:0 0 10px;padding-left:22px`,
  ol: `margin:0 0 10px;padding-left:22px`,
  li: `margin:0 0 3px`,
  a: `color:${LINK};font-weight:500`,
  code: `font-family:Consolas,Menlo,monospace;font-size:0.9em;background:${TINT};padding:1px 4px;border-radius:3px`,
  pre: `font-family:Consolas,Menlo,monospace;background:${TINT};padding:10px;border-radius:4px;overflow:auto`,
}

/**
 * The update's body as email-ready HTML: rendered with the same parser as the
 * page, then given inline styles. Only tags `renderMarkdown` itself emits are
 * touched, and its output is already escaped, so no text the coach typed is
 * ever re-interpreted as markup here.
 */
export function updateEmailHtml(update: Pick<TeamUpdate, 'body'>): string {
  const html = renderMarkdown(update.body).replace(
    /<(table|th|td|h2|h3|h4|p|ul|ol|li|a|code|pre)(\s[^>]*)?>/g,
    (_m, tag: string, attrs: string | undefined) => `<${tag}${attrs ?? ''} style="${STYLES[tag]}">`,
  )
  return `<div style="font-family:Segoe UI,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:${INK}">${html}</div>`
}

/** The subject the mail client opens with: the update's own title. */
export function updateEmailSubject(update: Pick<TeamUpdate, 'title'>): string {
  return update.title.trim()
}

/**
 * `mailto:` with the subject and no recipient — the team's address list lives
 * in the coach's mail client, not here, and guessing it from the roster would
 * mail people the coach did not mean to.
 */
export function updateMailtoHref(update: Pick<TeamUpdate, 'title'>): string {
  return `mailto:?subject=${encodeURIComponent(updateEmailSubject(update))}`
}

/**
 * Put the update on the clipboard as rich HTML, with the Markdown source as the
 * plain-text alternative for a client that will not take HTML.
 *
 * Must be called from the click itself: the async Clipboard API needs the user
 * gesture, and Safari refuses a write that starts after an `await`. The same
 * trap `lib/clipboard-image.ts` documents.
 *
 * `ClipboardItem` is missing in a non-secure context — reaching the tenant by
 * LAN IP rather than localhost, say — so there the selection-and-copy fallback
 * does the job instead. It still copies rich HTML in every browser we support.
 */
export async function copyUpdateToClipboard(update: Pick<TeamUpdate, 'body'>): Promise<void> {
  const html = updateEmailHtml(update)
  const text = update.body

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ])
    return
  }

  const holder = document.createElement('div')
  holder.contentEditable = 'true'
  holder.style.position = 'fixed'
  holder.style.left = '-10000px'
  holder.innerHTML = html
  document.body.appendChild(holder)
  try {
    const range = document.createRange()
    range.selectNodeContents(holder)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
    if (!document.execCommand('copy')) throw new Error('This browser would not copy to the clipboard.')
    selection?.removeAllRanges()
  } finally {
    holder.remove()
  }
}
